/*!
 * liveSessions.js — Oscilla Live's sessions, each at its own address
 * © 2026 Rob Canning — GPLv3
 *
 *   live.html#s=k3f9q2ab           a session: its code is kept in this browser
 *                                  under that name, and the address brings it back
 *   live.html?room=jam#s=k3f9q2ab  ... in a room
 *   live.html#code=<deflated>      the code itself, in the link (Share): opened, it
 *                                  becomes a session of its own, the link left as it was
 *
 * The console keeps each session's buffer (setBufferName, oscillaLive.js);
 * here is only the list of them -- which there are, the room each was in --
 * for the landing's Continue, newest first, the oldest let go past MAX.
 */

import { readBuffer, storeBuffer, dropBuffer } from "./oscillaLive.js";

const INDEX_KEY = "oscilla.liveSessions";
const MAX = 50;
const OLD_BUFFER = "unknown";        // the one buffer live.html kept before sessions

/** The name a session's buffer is kept under. */
export const bufferNameOf = id => `live:${id}`;

const readIndex = () => { try { return JSON.parse(localStorage.getItem(INDEX_KEY) || "[]"); } catch { return []; } };
const writeIndex = list => { try { localStorage.setItem(INDEX_KEY, JSON.stringify(list)); } catch { /* private mode */ } };

/** A new session's name: 8 letters and digits. */
export function newSessionId() {
  const a = crypto.getRandomValues(new Uint8Array(8));
  return [...a].map(b => "abcdefghijkmnpqrstuvwxyz23456789"[b % 32]).join("");
}

/** What the address asks for: { session } or { code } (still deflated), or {}. */
export function fromAddress() {
  const h = new URLSearchParams(location.hash.slice(1));
  if (h.get("code")) return { code: h.get("code") };
  if (/^[a-z0-9]{4,16}$/.test(h.get("s") ?? "")) return { session: h.get("s") };
  return {};
}

/** The address of a session (and the room it is in), on this page. */
export function sessionAddress(id, room = null) {
  const q = new URLSearchParams(location.search);
  if (room) q.set("room", room); else q.delete("room");
  const search = q.toString();
  return `${location.pathname}${search ? `?${search}` : ""}#s=${id}`;
}

/** This session is in use now (and in this room): first on the list. */
export function touchSession(id, room) {
  const list = readIndex().filter(e => e.id !== id);
  const prev = readIndex().find(e => e.id === id);
  list.unshift({ id, room: room === undefined ? prev?.room ?? null : room });
  for (const gone of list.splice(MAX)) dropBuffer(bufferNameOf(gone.id));
  writeIndex(list);
}

/** A first line to know a session by: the first that is not blank. */
function firstLine(text) {
  const line = String(text ?? "").split("\n").map(l => l.replace(/^\s*(\/\/+|#\$?)\s*/, "").trim()).find(Boolean);
  return line ? (line.length > 60 ? `${line.slice(0, 59)}…` : line) : "";
}

/** The sessions, newest first: { id, room, preview, updatedAt }. Empty ones left out. */
export function listSessions() {
  // the buffer live.html kept before there were sessions: the first one
  const old = readBuffer(OLD_BUFFER);
  if (!readIndex().length && old?.content?.trim()) {
    const id = newSessionId();
    storeBuffer(bufferNameOf(id), old.content, old.updatedAt);
    writeIndex([{ id, room: null }]);
  }
  return readIndex()
    .map(e => ({ ...e, ...(readBuffer(bufferNameOf(e.id)) || {}) }))
    .filter(e => e.content?.trim())
    .map(e => ({ id: e.id, room: e.room, preview: firstLine(e.content), updatedAt: e.updatedAt ?? null }));
}

// ---- the code in a link: deflate-raw, then base64url ----------------------

const toB64url = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromB64url = s => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/")), c => c.charCodeAt(0));

async function pipe(bytes, stream) {
  const out = new Response(new Blob([bytes]).stream().pipeThrough(stream));
  return new Uint8Array(await out.arrayBuffer());
}

/** A link with this code in it. */
export async function shareAddress(text) {
  const packed = await pipe(new TextEncoder().encode(text), new CompressionStream("deflate-raw"));
  return `${location.origin}${location.pathname}#code=${toB64url(packed)}`;
}

/** A shared link's code, as text -- or null when it cannot be read. */
export async function codeFrom(packed) {
  try { return new TextDecoder().decode(await pipe(fromB64url(packed), new DecompressionStream("deflate-raw"))); }
  catch { return null; }
}

/** A session holding this code (a shared link's), not yet on the list. */
export function sessionWith(text) {
  const id = newSessionId();
  storeBuffer(bufferNameOf(id), text);
  return id;
}
